/**
 * IssueActivityLog Component
 *
 * Displays issue activity for a project (created, updated, resolved)
 * with severity badges and scenario context.
 */

import { useState } from 'react';
import {
  AlertCircle,
  Download,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { EmptyState } from '@/components/EmptyState';
import { toast } from '@/lib/toast';
import { formatTimestamp, formatRelativeTime } from '@/lib/utils';
import { useIssueLogs } from '@/hooks/useActivityLogs';
import type { IssueLogEntry } from '@/types/domain';

interface IssueActivityLogProps {
  projectId: string;
}

const actionConfig: Record<string, { label: string; className: string }> = {
  created: { label: 'Created', className: 'bg-success/10 text-success' },
  updated: { label: 'Updated', className: 'bg-info/10 text-info' },
  resolved: { label: 'Resolved', className: 'bg-success/10 text-success' },
};

const severityConfig: Record<string, { label: string; variant: 'destructive' | 'default' | 'secondary' | 'outline' }> = {
  '1_Critical': { label: 'Critical', variant: 'destructive' },
  '2_High': { label: 'High', variant: 'default' },
  '3_Medium': { label: 'Medium', variant: 'secondary' },
  '4_Low': { label: 'Low', variant: 'outline' },
};

function IssueLogRow({ entry }: { entry: IssueLogEntry }) {
  const config = actionConfig[entry.action] ?? actionConfig.created;
  const severity = severityConfig[entry.details.severity] ?? { label: entry.details.severity, variant: 'outline' as const };

  return (
    <TableRow>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="space-y-0.5">
                <span className="text-sm">{formatTimestamp(entry.timestamp)}</span>
                <span className="block text-xs text-muted-foreground">{formatRelativeTime(entry.timestamp)}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {new Date(entry.timestamp).toLocaleString()}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={config.className}>
          {config.label}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{entry.actor_name}</TableCell>
      <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]" title={entry.scenario_name}>
        {entry.scenario_name}
      </TableCell>
      <TableCell className="text-sm font-medium truncate max-w-[200px]" title={entry.details.title}>
        {entry.details.title}
      </TableCell>
      <TableCell>
        <Badge variant={severity.variant}>
          {severity.label}
        </Badge>
      </TableCell>
    </TableRow>
  );
}

export function IssueActivityLog({ projectId }: IssueActivityLogProps) {
  const { data, isLoading } = useIssueLogs(projectId, { refetchInterval: 30000 });
  const [showAll, setShowAll] = useState(false);

  const handleExport = () => {
    const logs = data?.logs ?? [];
    const csv = [
      'Timestamp,Action,Actor,Scenario,Issue Title,Severity,Step',
      ...logs.map(log =>
        `"${new Date(log.timestamp).toISOString()}","${log.action}","${log.actor_name}","${log.scenario_name}","${log.details.title}","${log.details.severity}",${log.details.step_number}`
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `issue-activity-${projectId}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Issue activity exported to CSV');
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Issue Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const logs = data?.logs ?? [];

  if (logs.length === 0) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="No Issue Activity"
        description="Issue activity will appear here when issues are created or updated."
      />
    );
  }

  const displayLogs = showAll ? logs : logs.slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-primary" />
              Issue Activity
            </CardTitle>
            <CardDescription>
              {logs.length} issue event{logs.length !== 1 ? 's' : ''}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExport}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[160px]">Time</TableHead>
              <TableHead className="w-[100px]">Action</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Scenario</TableHead>
              <TableHead>Issue</TableHead>
              <TableHead className="w-[90px]">Severity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayLogs.map((entry, idx) => (
              <IssueLogRow key={`${entry.timestamp}-${idx}`} entry={entry} />
            ))}
          </TableBody>
        </Table>
        {logs.length > 5 && (
          <div className="mt-3 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAll(!showAll)}
              className="text-muted-foreground"
            >
              {showAll ? 'Show less' : `Show all ${logs.length} entries`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
